/**
 * Firebase Auth client (iter-93) — replaces the old Emergent Google flow.
 *
 * Web Google + Apple → Firebase JS SDK popup flow.
 * Native iOS Apple → @capacitor-community/apple-sign-in plugin so the user
 * gets the real iOS Sign In with Apple sheet instead of a web popup.
 *
 * In every flow we end up with an OIDC id_token that we POST to our
 * FastAPI backend, which verifies it with firebase_admin and mints our
 * own app JWT. Firebase itself is purely the upstream identity provider.
 */
import { initializeApp, getApps, getApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signOut as fbSignOut,
  setPersistence,
  browserLocalPersistence,
} from 'firebase/auth';
import { Capacitor } from '@capacitor/core';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

function getFirebaseApp() {
  return getApps().length ? getApp() : initializeApp(firebaseConfig);
}

export function getFirebaseAuth() {
  const auth = getAuth(getFirebaseApp());
  // Best-effort: keep the user logged into Firebase across reloads. We still
  // sign them out of Firebase immediately after exchanging tokens with our
  // backend (we use Firebase only for the initial identity proof).
  try { setPersistence(auth, browserLocalPersistence); } catch { /* ignore */ }
  return auth;
}

// ─── Google ─────────────────────────────────────────────────────────────────

/**
 * Single attempt at native Google sign-in with a hard timeout.
 * Extracted into its own function so googleSignInNative() below can
 * cheaply retry it (see iter-99 cold-start handling).
 */
async function signInWithGoogleNativeAttempt(FirebaseAuthentication, timeoutMs, attemptLabel) {
  console.log(`[firebaseAuth] googleSignInNative[${attemptLabel}]: calling FirebaseAuthentication.signInWithGoogle()`);
  return Promise.race([
    FirebaseAuthentication.signInWithGoogle(),
    new Promise((_, reject) => setTimeout(
      () => {
        const err = new Error(`Google sign-in TIMEOUT after ${timeoutMs}ms (${attemptLabel}) — the native picker never returned.`);
        err.code = 'NATIVE_TIMEOUT';
        reject(err);
      },
      timeoutMs,
    )),
  ]);
}

async function googleSignInNative() {
  // iter-94: signInWithPopup hangs inside the Capacitor iOS WebView because
  // the OAuth redirect can't return to the capacitor:// origin. Use the
  // official Capacitor Firebase plugin which calls the iOS Google Sign-In
  // SDK natively and returns a Firebase ID token we can verify on backend.
  console.log('[firebaseAuth] googleSignInNative: starting');
  const mod = await import('@capacitor-firebase/authentication');
  const FirebaseAuthentication = mod.FirebaseAuthentication;

  // iter-99: GoogleSignIn 7.x on iOS sometimes hangs on the very first
  // signIn(withPresenting:) invocation because URL-handler / ASWebAuthen-
  // ticationSession plumbing is registered LAZILY on first call. An
  // immediate retry succeeds because the SDK has now warmed up.
  // AppDelegate already calls restorePreviousSignIn at launch to mitigate
  // this, but if cold-start still happens we transparently retry once with
  // a tight 8 s budget so the user never sees the 30 s hang.
  const FIRST_ATTEMPT_TIMEOUT_MS = 8000;
  const RETRY_ATTEMPT_TIMEOUT_MS = 25000;
  let result;
  try {
    try {
      result = await signInWithGoogleNativeAttempt(FirebaseAuthentication, FIRST_ATTEMPT_TIMEOUT_MS, 'attempt1');
    } catch (firstErr) {
      // Only retry on cold-start TIMEOUT, never on real auth errors
      // (popup-closed, network-failed, invalid-credential …). Real
      // errors should surface to the user immediately.
      if (firstErr?.code !== 'NATIVE_TIMEOUT') {
        throw firstErr;
      }
      console.log('[firebaseAuth] googleSignInNative: first attempt timed out — retrying once after SDK warm-up');
      // Tiny pause lets the SDK finish whatever it was warming up.
      await new Promise((r) => setTimeout(r, 250));
      result = await signInWithGoogleNativeAttempt(FirebaseAuthentication, RETRY_ATTEMPT_TIMEOUT_MS, 'attempt2');
    }
    console.log('[firebaseAuth] googleSignInNative: signInWithGoogle returned', JSON.stringify(result).slice(0, 400));
  } catch (e) {
    console.log('[firebaseAuth] googleSignInNative: signInWithGoogle THREW', e?.code, e?.message, JSON.stringify(e).slice(0, 400));
    throw e;
  }
  // The plugin returns { user, credential: { idToken, ... } }. The Firebase
  // ID token (a JWT signed by Firebase, audience = our Firebase project) is
  // exposed via getIdToken() AFTER the native sign-in completes.
  let tokRes = null;
  try {
    tokRes = await FirebaseAuthentication.getIdToken({ forceRefresh: false });
    console.log('[firebaseAuth] googleSignInNative: getIdToken returned, len=', tokRes?.token?.length);
  } catch (e) {
    console.log('[firebaseAuth] googleSignInNative: getIdToken THREW', e?.code, e?.message);
  }
  const idToken = tokRes?.token || result?.credential?.idToken;
  if (!idToken) {
    console.log('[firebaseAuth] googleSignInNative: NO ID TOKEN', { result, tokRes });
    throw new Error('Google sign-in: no id_token returned by native plugin');
  }
  // Sign out of native Firebase session immediately — backend JWT is our
  // source of truth.
  try { await FirebaseAuthentication.signOut(); } catch { /* ignore */ }
  return { idToken };
}

async function googleSignInWeb() {
  const auth = getFirebaseAuth();
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  const result = await signInWithPopup(auth, provider);
  const idToken = await result.user.getIdToken(false);
  try { await fbSignOut(auth); } catch { /* ignore */ }
  return { idToken };
}

export async function signInWithGoogle() {
  // iter-96: ANY native Capacitor platform (iOS + Android) must go through
  // the native plugin. The web popup flow opens an external browser inside
  // the Android WebView and the post-login redirect never returns to the
  // app → white screen. Only the actual web (Safari/Chrome on desktop or
  // becaim.app in a browser) should use the Firebase JS popup flow.
  if (Capacitor.isNativePlatform()) {
    return googleSignInNative();
  }
  return googleSignInWeb();
}

// ─── Apple ──────────────────────────────────────────────────────────────────
// Native iOS path uses the Capacitor plugin; everywhere else falls back to
// the Firebase web popup.

async function appleSignInNative() {
  // Lazy-import the plugin so non-iOS builds don't pay the cost.
  const mod = await import('@capacitor-community/apple-sign-in');
  const SignInWithApple = mod.SignInWithApple;
  const options = {
    clientId: process.env.REACT_APP_APPLE_SERVICE_ID || '',
    redirectURI: '',
    scopes: 'email name',
    state: 'becaim',
    nonce: 'becaim-' + Math.random().toString(36).slice(2),
  };
  const res = await SignInWithApple.authorize(options);
  // res.response: { identityToken, authorizationCode, user, email, givenName, familyName }
  const r = res.response || {};
  return {
    idToken: r.identityToken,
    fullName: [r.givenName, r.familyName].filter(Boolean).join(' ') || null,
    email: r.email || null,
    apple_sub: r.user || null,
    via: 'native',
  };
}

async function appleSignInWeb() {
  const auth = getFirebaseAuth();
  const provider = new OAuthProvider('apple.com');
  provider.addScope('email');
  provider.addScope('name');
  const result = await signInWithPopup(auth, provider);
  const idToken = await result.user.getIdToken(false);
  // Apple only returns the display name on the very first sign-in via
  // the AdditionalUserInfo profile. firebase wraps that in result._tokenResponse
  // on web — we pass what we can.
  const displayName = result.user.displayName || null;
  const email = result.user.email || null;
  try { await fbSignOut(auth); } catch { /* ignore */ }
  return { idToken, fullName: displayName, email, via: 'web' };
}

export async function signInWithApple() {
  if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
    return appleSignInNative();
  }
  return appleSignInWeb();
}
